/**
 * The legacy Survivor status bar: Status, Submitted, Lock, the missed pick
 * note, and the tab's one primary action, Save (S5).
 */
import { Button, cn } from "@sportsmet/ui";
import type { JSX } from "react";

import {
  SURVIVOR_LOCK_LABEL,
  SURVIVOR_STATUS_LABEL,
  SURVIVOR_SUBMITTED_LABEL,
} from "./survivorGameCopy";
import { STATUS_TONE_CLASS, type typeStatusTone } from "./survivorStatusModel";

export type typeSurvivorPrimaryAction = {
  /** The Save button's words. An empty or whitespace only label renders no Save button at all (ISSUE-948). */
  label: string;
  enabled: boolean;
  isSubmitting: boolean;
  onPress: () => void;
};
export type typeSurvivorStatusBarProps = {
  statusLabel: string;
  statusTone: typeStatusTone;
  submittedLabel: string | null;
  lockLabel: string | null;
  missedPickNote: string | null;
  primaryAction: typeSurvivorPrimaryAction | null;
};

export function SurvivorStatusBar(
  props: typeSurvivorStatusBarProps,
): JSX.Element {
  const primaryAction =
    props.primaryAction !== null && props.primaryAction.label.trim() !== ""
      ? props.primaryAction
      : null;
  return (
    <div
      data-testid="survivor-status-bar"
      className="flex flex-col gap-2 border-t border-hairline bg-surface py-3"
    >
      <dl className="grid grid-cols-3 gap-2">
        <div>
          <dt className="text-caption text-ink-secondary">
            {SURVIVOR_STATUS_LABEL}
          </dt>
          <dd
            data-testid="survivor-status-value"
            className={cn(
              "text-body-strong",
              STATUS_TONE_CLASS[props.statusTone],
            )}
          >
            {props.statusLabel}
          </dd>
        </div>
        {props.submittedLabel !== null && (
          <div>
            <dt className="text-caption text-ink-secondary">
              {SURVIVOR_SUBMITTED_LABEL}
            </dt>
            <dd
              data-testid="survivor-status-submitted"
              className="text-body-strong text-ink tabular-nums"
            >
              {props.submittedLabel}
            </dd>
          </div>
        )}
        {props.lockLabel !== null && (
          <div>
            <dt className="text-caption text-ink-secondary">
              {SURVIVOR_LOCK_LABEL}
            </dt>
            <dd
              data-testid="survivor-status-lock"
              className="text-body-strong text-ink"
            >
              {props.lockLabel}
            </dd>
          </div>
        )}
      </dl>
      {props.missedPickNote !== null && (
        <p
          data-testid="survivor-missed-pick-note"
          className="text-caption text-ink-secondary"
        >
          {props.missedPickNote}
        </p>
      )}
      {primaryAction !== null && (
        <Button
          variant="primary"
          fullWidth
          data-testid="survivor-game-save"
          disabled={!primaryAction.enabled}
          loading={primaryAction.isSubmitting}
          onClick={primaryAction.onPress}
        >
          {primaryAction.label}
        </Button>
      )}
    </div>
  );
}
